import apiService from '@/services/api'
import type { User } from '@/types'
import { action, makeAutoObservable } from 'mobx'

class AuthStore {
  user: User | null = null
  token: string | null = null
  isLoading = false

  constructor() {
    makeAutoObservable(this)
    this.loadFromStorage()
  }

  get isAuthenticated() {
    return !!this.token && !!this.user
  }

  get isAdmin() {
    return this.user?.role === 'Admin'
  }

  get isDeveloper() {
    return this.user?.role === 'Developer'
  }

  @action
  setUser(user: User) {
    this.user = user
  }

  @action
  setToken(token: string) {
    this.token = token
    localStorage.setItem('token', token)
  }

  @action
  setLoading(loading: boolean) {
    this.isLoading = loading
  }

  login = async (username: string, password: string) => {
    this.setLoading(true)
    try {
      const res = await apiService.login(username, password)

      // 检查登录是否真正成功 - 后端返回的是 { code, info, data: { accessToken, expiresIn } }
      if (res && res.code === '200' && res.data && res.data.accessToken) {
        // 登录成功时清除所有store的状态
        await this.clearAllStores()

        this.setToken(res.data.accessToken)
        this.setUser({ username } as User)
        this.saveToStorage()

        // 登录成功后，确保固件管理菜单展开
        const { default: layoutStore } = await import('./layoutStore')
        layoutStore.setOpenKeys(['/firmware'])

        return { success: true }
      } else {
        // 登录失败，抛出错误
        throw new Error(res.info || res.message || '登录失败，请检查用户名和密码')
      }
    } catch (error: any) {
      // 确保抛出错误，让上层组件知道登录失败
      throw new Error(error?.message || '登录失败，请检查用户名和密码')
    } finally {
      this.setLoading(false)
    }
  }

  logout = async () => {
    // 退出时清除所有store的状态
    await this.clearAllStores()
    
    this.user = null
    this.token = null
    this.clearStorage()
  }

  private loadFromStorage() {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr)
        this.token = token
        this.user = user
      } catch (error) {
        this.clearStorage()
      }
    }
  }

  private saveToStorage() {
    if (this.user && this.token) {
      localStorage.setItem('token', this.token)
      localStorage.setItem('user', JSON.stringify(this.user))
    }
  }

  private clearStorage() {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  // 清除所有store的状态
  private clearAllStores = async () => {
    // 清除tabStore状态
    const { tabStore } = await import('./tabStore')
    // 重置tabStore到初始状态
    tabStore.tabs = [
      {
        key: '/firmware/list',
        label: '小包列表',
        path: '/firmware/list',
        closable: false, // 固件列表不可关闭
      },
    ]
    tabStore.activeKey = '/firmware/list'

    // 清除layoutStore状态
    const { default: layoutStore } = await import('./layoutStore')
    layoutStore.openKeys = []

    // 如果当前路由不是固件列表，强制替换到固件列表，防止在退出登录时留下其他标签
    if (typeof window !== 'undefined' && window.location.pathname !== '/firmware/list') {
      window.history.replaceState(null, '', '/firmware/list')
    }
  }
}

const authStore = new AuthStore()

export const useAuthStore = () => authStore
export default authStore
